/**
 * Resolves the contact's currently-active engagement. A client can run
 * multiple engagements concurrently (Retainer + Project + Advisory); the
 * portal switcher lets the contact swap focus between them.
 *
 * Persistence: localStorage (per-device). On first run / unknown id, falls
 * back to the most-recently-created ACTIVE engagement.
 */
import { useEffect, useState, useMemo } from 'react';
import { trpc } from '../lib/trpc.js';
import { useAuth } from '../lib/auth.js';

const STORAGE_KEY = 'scope.portal.activeEngagementId';

function readStored(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export interface ActiveEngagement {
  id: string;
  name: string;
  type: 'RETAINER' | 'PROJECT' | 'ADVISORY';
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  clientId: string;
  clientName: string;
  agencyTz: string;
  agencyVatRate: number | null;
}

export interface UseActiveEngagementResult {
  engagement: ActiveEngagement | null;
  engagements: ActiveEngagement[];
  isLoading: boolean;
  isError: boolean;
  setActiveEngagementId: (id: string) => void;
}

export function useActiveEngagement(): UseActiveEngagementResult {
  const { auth } = useAuth();
  const listQ = trpc.engagement.listMine.useQuery(undefined, {
    enabled: auth.kind === 'contact',
    staleTime: 60_000,
  });
  const [activeId, setActiveId] = useState<string | null>(() => readStored());

  const engagements = useMemo<ActiveEngagement[]>(() => {
    return (listQ.data ?? []).map((e) => ({
      id: e.id,
      name: e.name,
      type: e.type,
      status: e.status,
      clientId: e.clientId,
      clientName: e.client.companyName,
      agencyTz: e.client.agency.timezone,
      agencyVatRate: e.client.agency.defaultVatRate,
    }));
  }, [listQ.data]);

  // Reconcile stored id with the actual list: if the stored one is gone,
  // fall back to the first ACTIVE engagement (or just the first one).
  useEffect(() => {
    if (engagements.length === 0) return;
    if (activeId && engagements.some((e) => e.id === activeId)) return;
    const fallback = engagements.find((e) => e.status === 'ACTIVE') ?? engagements[0]!;
    setActiveId(fallback.id);
    writeStored(fallback.id);
  }, [engagements, activeId]);

  const engagement = engagements.find((e) => e.id === activeId) ?? engagements[0] ?? null;

  return {
    engagement: engagement ?? null,
    engagements,
    isLoading: listQ.isLoading,
    isError: listQ.isError,
    setActiveEngagementId: (id: string) => {
      setActiveId(id);
      writeStored(id);
    },
  };
}
